import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import scrollToTop from "utils/scrollToTop";
import useCreateUrl from "hooks/useCreateUrl";
import { SEARCH_PAGE_SIZE } from "@flights/constants";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { PaginationProps } from "@bookingcom/bui-react";
import { actions } from "../../../store/search/actions";
import { useLocation, useHistory } from "react-router-dom";
import { useGetAvailableFilters } from "../SearchResults/components/boundsFilterHooks";

export default function useSearchPaginationController() {
  const i18n = useI18n();
  const dispatch = useDispatch();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const { enhanceURLSearchParams } = useCreateUrl();
  const { totalCount, filteredTotalCount } = useGetAvailableFilters();

  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const totalPages = useMemo(() => {
    const total = totalCount ? Math.ceil(totalCount / SEARCH_PAGE_SIZE) : 0;
    return filteredTotalCount ? Math.ceil(filteredTotalCount / SEARCH_PAGE_SIZE) : total;
  }, [totalCount, filteredTotalCount]);

  const currentPage = useMemo(() => {
    return Number(qs.get("page") || 1);
  }, [qs]);

  const onPageChange = useCallback<NonNullable<PaginationProps["onPageChange"]>>(
    ({ page }) => {
      const target = Math.max(1, Math.min(page, totalPages));
      dispatch(actions.setPage(target));
      scrollToTop();
      qs.set("page", `${target}`);
      enhanceURLSearchParams(qs);
      history.push({ pathname: pathname, search: qs.toString() }, { hideLoader: true });
    },
    [qs, history, dispatch, pathname, enhanceURLSearchParams, totalPages]
  );

  const canRender = useMemo(() => totalPages > 1, [totalPages]);

  return {
    canRender,
    totalPages,
    currentPage,
    onPageChange,
    ariaPageLabelPrefix: "",
    ariaPreviousLabel: i18n.trans(t("flights_action_back")),
    ariaNextLabel: i18n.trans(t("flights_action_next"))
  };
}
